import { getSupabase } from 'shared/supabase/client';
import type { BatchLiveSession } from 'types';

export interface MyBatchEnrollment {
  enrollmentId: string;
  batchId: string;
  courseId: string | null;
  institutionId: string;
  schoolSlug: string | null;
  status: 'active' | 'inactive';
}

async function getPlatformUserId(): Promise<string | null> {
  const supabase = getSupabase();
  const { data: session } = await supabase.auth.getSession();
  if (!session.session) return null;

  const { data: profile } = await supabase
    .from('core_users')
    .select('id')
    .eq('auth_user_id', session.session.user.id)
    .is('deleted_at', null)
    .maybeSingle();

  return profile?.id ?? null;
}

export async function fetchMyBatchEnrollment(batchId: string): Promise<MyBatchEnrollment | null> {
  const userId = await getPlatformUserId();
  if (!userId) return null;

  const { data, error } = await getSupabase()
    .from('inst_batch_enrollments')
    .select(`
      id,
      status,
      institution_id,
      inst_batches!inner (
        id,
        course_id,
        core_schools ( slug )
      )
    `)
    .eq('user_id', userId)
    .eq('batch_id', batchId)
    .is('deleted_at', null)
    .in('status', ['active', 'inactive'])
    .maybeSingle();

  if (error) throw error;
  if (!data) return null;

  const batchRaw = data.inst_batches as unknown;
  const batch = (Array.isArray(batchRaw) ? batchRaw[0] : batchRaw) as {
    id: string;
    course_id: string | null;
    core_schools: { slug: string } | { slug: string }[] | null;
  };
  const schoolRaw = batch.core_schools;
  const school = Array.isArray(schoolRaw) ? schoolRaw[0] : schoolRaw;

  return {
    enrollmentId: data.id as string,
    batchId: batch.id,
    courseId: batch.course_id,
    institutionId: data.institution_id as string,
    schoolSlug: school?.slug ?? null,
    status: data.status as 'active' | 'inactive',
  };
}

export async function fetchMyBatchLiveSchedules(batchId: string): Promise<BatchLiveSession[]> {
  const enrollment = await fetchMyBatchEnrollment(batchId);
  if (!enrollment) return [];

  const { data, error } = await getSupabase()
    .from('inst_batch_schedules')
    .select(
      'id, batch_id, title, scheduled_at, duration_minutes, meeting_url, meeting_provider, recording_url, recording_expires_at'
    )
    .eq('batch_id', batchId)
    .is('deleted_at', null)
    .order('scheduled_at', { ascending: true });

  if (error) throw error;

  const nowMs = Date.now();

  return (data ?? []).map((row) => {
    const recordingExpiresAt = (row.recording_expires_at as string | null) ?? null;
    const recordingExpired =
      Boolean(recordingExpiresAt) && new Date(recordingExpiresAt as string).getTime() < nowMs;

    return {
      id: row.id,
      batchId: row.batch_id,
      title: row.title,
      scheduledAt: row.scheduled_at,
      durationMinutes: row.duration_minutes,
      meetingUrl: row.meeting_url ?? null,
      meetingProvider: (row.meeting_provider as 'zoom' | 'google_meet' | 'custom') ?? 'custom',
      // Hide expired recordings in the supported learner UI. This is not DRM:
      // permanent URL revocation requires provider-side expiry or protected storage.
      recordingUrl: recordingExpired ? null : row.recording_url ?? null,
      recordingExpiresAt,
    };
  });
}
